import { useState } from "react";
import { getSupabase } from "@/lib/supabase/client";
import { ArrowLeft, Mail, Loader2, CheckCircle } from "lucide-react";
import { useCurrentModal, modalActions } from "@/stores/modal-store";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function ForgotPasswordModal() {
  const currentModal = useCurrentModal();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isOpen = currentModal === "forgot";

  const resetForm = () => {
    setEmail("");
    setMsg(null);
    setErr(null);
    setBusy(false);
    setSuccess(false);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setMsg(null);
    setSuccess(false);

    if (!/\S+@\S+\.\S+/.test(email)) {
      return setErr("Enter a valid email.");
    }

    setBusy(true);
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      if (error) throw error;

      setSuccess(true);
      setMsg("If that email exists, we've sent a reset link.");
      setEmail("");

      setTimeout(() => {
        modalActions.close();
      }, 3000);
    } catch (e: any) {
      setErr(e?.message ?? "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={modalActions.close}>
      <DialogContent className="sm:max-w-md">
        <div className="relative p-6">
          <button
            onClick={() => {
              resetForm();
              modalActions.open("signin");
            }}
            className="absolute left-6 top-6 h-10 w-10 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors group"
            aria-label="Back to sign in"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500 group-hover:text-gray-700" />
          </button>

          <DialogHeader className="text-center pt-2 pb-6">
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Reset password
            </DialogTitle>
            <DialogDescription className="text-gray-500 text-sm mt-1">
              Enter your email to receive a reset link
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="relative group">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy || success}
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={busy || success}
              className="w-full py-3.5 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 active:scale-[0.99] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : success ? (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Sent!
                </>
              ) : (
                "Send reset link"
              )}
            </button>

            {msg && success && (
              <div className="p-4 bg-green-50 border border-green-100 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="text-left">
                    <p className="text-green-700 font-medium">{msg}</p>
                    <p className="text-green-600 text-sm mt-1">
                      Check your inbox and follow the instructions.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {err && (
              <div className="p-4 bg-red-50 border border-red-100 rounded-lg animate-fade-in">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <div className="h-5 w-5 rounded-full bg-red-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                  </div>
                  <div className="text-left">
                    <p className="text-red-700 font-medium">{err}</p>
                    <p className="text-red-600 text-sm mt-1">
                      Please check the email address and try again.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="text-center text-sm text-gray-600">
              Remember your password?{" "}
              <button
                onClick={() => {
                  resetForm();
                  modalActions.open("signin");
                }}
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
